import type { ColumnPinningPosition, ColumnPinningState } from '@tanstack/react-table';
import { useEffectOnValueChange } from '@timelyapp/react';
import type { TimesheetScopes } from 'components/timesheets/types';
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'constants/defaults';
import { findKey, mapValues } from 'lodash-es';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { cloneDeep } from 'utils';
import { getLocalStorageObject, setLocalStorageObject } from 'utils/localStorage';
import { groupBy, keyBy, notNull } from 'utils/typesafe';
import { DEFAULT_CLIENT_DATATABLE_SETTINGS, DEFAULT_DATATABLE_SETTINGS } from './constants';
import type {
  ColumnState,
  DatatableSettings,
  DatatableSettingsKey,
  DatatableSettingsState,
  DatatableState,
} from './datatable.types';
import { isValidDatatableState } from './datatable.types';

/**
 * Custom hook to create and (optionally) persist column state and other settings of a datatable.
 * @param defaultState
 * @param localStorageKey (optional) If set, settings will be saved / loaded from local storage
 * @returns states and state setters
 */
export const useDatatableState = (
  defaultState: Readonly<DatatableState>,
  localStorageKey?: string,
  scope?: TimesheetScopes | undefined
) => {
  const { initialOrder, initialPinning, initialVisibility, initialSettings } = useMemo(() => {
    const state = cloneDeep<DatatableState>(defaultState);

    if (localStorageKey) {
      const persistedState = getLocalStorageObject<DatatableState>(localStorageKey);
      if (persistedState && isValidDatatableState(persistedState)) {
        state.columns = mergeColumnStates(defaultState, persistedState);
        state.settings = mergeDatatableSettings(defaultState.settings, persistedState.settings);
      }
    }

    const {
      order: initialOrder,
      visibility: initialVisibility,
      pinning: initialPinning,
    } = decomposeColumnState(state.columns);

    return {
      initialOrder,
      initialPinning,
      initialVisibility,
      initialSettings: extractSettingsValues(state.settings || (EMPTY_OBJECT as DatatableSettings)),
    };
  }, [defaultState, localStorageKey]);

  const [columnOrder, setColumnOrder] = useState(initialOrder);
  const [columnPinning, setColumnPinning] = useState(initialPinning);
  const [columnVisibility, setColumnVisibility] = useState(initialVisibility);
  const [datatableSettings, setDatatableSettings] = useState(initialSettings);

  const buildStorageObject = useCallback((): DatatableState => {
    const state: DatatableState = {
      columns: columnOrder.map(id => {
        const column: ColumnState = {
          id,
          isVisible: !!columnVisibility[id],
          pinned: (findKey(columnPinning, c => c?.includes(id)) as ColumnPinningPosition) || false,
        };
        const updatedAt = defaultState.columns.find(c => c.id === column.id)?.updatedAt;
        if (updatedAt) {
          column.updatedAt = updatedAt;
        }
        return column;
      }),
      settings: Object.keys(datatableSettings).reduce(
        (settings, _key) => {
          const key = _key as DatatableSettingsKey;
          settings[key] = { value: datatableSettings[key] };
          if (defaultState.settings && defaultState.settings[key]?.updatedAt) {
            settings[key].updatedAt = defaultState.settings[key].updatedAt;
          }
          return settings;
        },
        {} as Record<string, any>
      ),
    };

    if (defaultState.columnOrderUpdatedAt) {
      state.columnOrderUpdatedAt = defaultState.columnOrderUpdatedAt;
    }

    return state;
  }, [
    columnOrder,
    datatableSettings,
    defaultState.columnOrderUpdatedAt,
    defaultState.columns,
    defaultState.settings,
    columnVisibility,
    columnPinning,
  ]);

  const commitStateToLocalStorage = useCallback(() => {
    if (localStorageKey) {
      setLocalStorageObject(localStorageKey, buildStorageObject());
    }
  }, [buildStorageObject, localStorageKey]);

  const setDatatableSetting = useCallback((key: DatatableSettingsKey, value: any) => {
    setDatatableSettings(settings => {
      return { ...settings, [key]: value };
    });
  }, []);

  const resetDatatableState = useCallback(() => {
    const { order, pinning, visibility } = decomposeColumnState(defaultState.columns);
    setColumnOrder(order);
    setColumnPinning(pinning);
    setColumnVisibility(visibility);
    setDatatableSettings(
      extractSettingsValues(
        scope === undefined ? DEFAULT_CLIENT_DATATABLE_SETTINGS : DEFAULT_DATATABLE_SETTINGS
      )
    );
  }, [defaultState.columns, scope]);

  useEffectOnValueChange(
    {
      columnOrder,
      columnPinning,
      columnVisibility,
      datatableSettings,
    },
    () => {
      commitStateToLocalStorage();
    },
    false
  );

  return {
    columnOrder,
    setColumnOrder,
    columnPinning,
    setColumnPinning,
    columnVisibility,
    setColumnVisibility,
    resetDatatableState,
    datatableSettings,
    setDatatableSettings,
    setDatatableSetting,
  };
};

function mergeColumnStates(
  defaultState: DatatableState,
  persistedState: DatatableState
): DatatableState['columns'] {
  const persistedOrder = persistedState.columns.map(c => c.id);

  const columnMap = new Map();
  defaultState.columns.forEach((col, index) => {
    const persistedIndex = persistedState.columns.findIndex(c => c.id === col.id);
    const persistedCol = persistedState.columns[persistedIndex];
    // column was added, try to inject it at the correct index
    if (!persistedCol) {
      persistedOrder.splice(index, 0, col.id);
    }

    if (persistedCol && !shouldUseDefaultState(col.updatedAt, persistedCol.updatedAt)) {
      columnMap.set(col.id, persistedCol);
    } else {
      columnMap.set(col.id, col);
    }
  });

  const useDefaultColumnOrder = shouldUseDefaultState(
    defaultState.columnOrderUpdatedAt,
    persistedState.columnOrderUpdatedAt
  );

  return useDefaultColumnOrder
    ? defaultState.columns.map(c => columnMap.get(c.id))
    : persistedOrder.map(id => columnMap.get(id)).filter(notNull);
}

function mergeDatatableSettings(
  defaultSettings: DatatableSettings | undefined,
  persistedSettings: DatatableSettings | undefined
): DatatableSettings {
  let mergedSettings = {} as any;
  if (defaultSettings) {
    if (persistedSettings) {
      Object.keys(defaultSettings).forEach(_key => {
        const key = _key as DatatableSettingsKey;

        const defaultSetting = defaultSettings[key];
        const persistedSetting = persistedSettings[key];

        if (
          shouldUseDefaultState(defaultSetting?.updatedAt, persistedSetting?.updatedAt) ||
          persistedSetting === undefined
        ) {
          mergedSettings[key] = { ...defaultSetting };
        } else {
          mergedSettings[key] = { ...persistedSetting };
        }
      });
    } else {
      mergedSettings = { ...defaultSettings };
    }
  }

  return mergedSettings;
}

function decomposeColumnState(state: Array<ColumnState>) {
  const pinGroups = groupBy(state, 'pinned');

  return {
    order: state.map(s => s.id),
    pinning: {
      left: pinGroups.left ? pinGroups.left.map(item => item.id) : EMPTY_ARRAY,
      right: pinGroups.right ? pinGroups.right.map(item => item.id) : EMPTY_ARRAY,
    } as ColumnPinningState,
    visibility: mapValues(keyBy(state, 'id'), 'isVisible'),
  };
}

/**
 * Determine whether we overwrite the user's persisted property with the current default value
 */
function shouldUseDefaultState(
  defaultUpdatedAt: string | undefined,
  persistedUpdatedAt: string | undefined
) {
  // If the default state has no updatedAt property, this feature is not in use
  if (!defaultUpdatedAt) return false;

  // updatedAt value shouldn't be in the future
  const nowUtc = DateTime.now().toUTC();
  const defaultDateTime = DateTime.fromISO(defaultUpdatedAt);
  if (defaultDateTime > nowUtc) return false;

  // If the default updatedAt value is greater than the persisted one, we should overwrite the persisted state
  return !persistedUpdatedAt || defaultDateTime > DateTime.fromISO(persistedUpdatedAt);
}

function extractSettingsValues(settings: DatatableSettings) {
  const state: DatatableSettingsState = {};

  for (const _key in settings) {
    const key = _key as DatatableSettingsKey;
    const setting = settings[key];
    if (setting) {
      // @ts-expect-error
      state[key] = setting.value;
    }
  }
  return state;
}
